import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link, Navigate, useLocation, useNavigate, useParams } from "react-router";
import { useNative } from "../../../shared/phone/media";
import { BackLink } from "../../components/ui/BackLink";
import { Icon } from "../../components/ui/Icon";
import { Info } from "../../components/ui/Info";
import { api } from "../../lib/api";
import { ccApi } from "../../lib/ccApi";
import { connectionReady, shownError, shownState } from "../../lib/connectionsApi";
import { goalsApi, type GoalView } from "../../lib/goalsApi";
import { drafting, readyApi, type ReadyView } from "../../lib/readyApi";
import { CxRows } from "../connections/CxRows";
import { useConnections } from "../connections/useConnections";
import { ProductMark, type ProductRef } from "../goal/FlowHead";
import { mailStart } from "../goal/Review";
import { longDay, planDay } from "../goal/text";
import { useToast } from "../goal/useToast";
import { ColdEmail, listsBuilding, listsReady } from "./ColdEmail";
import { ReadyNative } from "./ReadyNative";
import type { ChangeSending } from "./Sending";
import "../../styles/cx.css";
import "../../styles/ready.css";

/**
 * Get ready for a goal's cold email (node G2): the mailbox connection, each market's list, and the emails
 * the owner approves before anything is sent. A goal's Meta ads get ready on their own steps, Audience and
 * Ads, with Launch at the bottom of Ads.
 */

const APPROVED = ["getting_ready", "live", "paused", "done"];
const POLL_MS = 5000;

const message = shownError;

const jump = (id: string) => document.getElementById(`rd-${id}`)?.scrollIntoView({ behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth", block: "start" });

interface Part {
  id: string;
  name: string;
  ok: boolean;
  agent: boolean;
}

function Who({ p }: { p: Part }) {
  if (p.ok)
    return (
      <span className="pill good">
        <Icon name="check" />
        Ready
      </span>
    );
  return p.agent ? <span className="pill live">The agent is on it</span> : <span className="pill tight">Waiting for you</span>;
}

function useProduct(productId: string | null) {
  const [product, setProduct] = useState<ProductRef | null>(null);
  useEffect(() => {
    if (!productId) return;
    api.products.list().then(
      (r) => {
        const p = r.products.find((x) => x.id === productId);
        setProduct(p ? { id: p.id, name: p.name, logoR2Key: p.logoR2Key, coldEmail: p.coldEmail } : null);
      },
      () => setProduct(null),
    );
  }, [productId]);
  return product;
}

export function GetReady() {
  const { goalId = "" } = useParams();
  const native = useNative();
  const { toast, say } = useToast();
  const [view, setView] = useState<GoalView | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [ready, setReady] = useState<ReadyView | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Why: approving the plan lands here with its note, said once.
  const said = (location.state as { said?: string } | null)?.said;
  useEffect(() => {
    if (!said) return;
    say(said);
    navigate(location.pathname, { replace: true, state: null });
  }, [said, say, navigate, location.pathname]);

  useEffect(() => {
    goalsApi.get(goalId).then(
      (v) => {
        setView(v);
        setLoadError(null);
      },
      (e) => setLoadError(message(e)),
    );
  }, [goalId]);

  const g = view?.goal;
  const approved = !!g && APPROVED.includes(g.state) && !!view?.current;
  const product = useProduct(g?.productId ?? null);
  const { conns, reload: reloadCx } = useConnections(approved ? (g?.productId ?? null) : null);

  const loadReady = useCallback(async () => {
    try {
      setReady(await readyApi.get(goalId));
    } catch (e) {
      setLoadError(message(e));
    }
  }, [goalId]);

  useEffect(() => {
    if (approved) void loadReady();
  }, [approved, loadReady]);

  // --- Read again while the agent or a send is at work -----------------------------------------
  const agentBusy = !!ready && (drafting(ready.runs.emails) || listsBuilding(ready.lists));
  const sending = !!ready?.emailSends.some((x) => x.lastSend && ["approved", "executing"].includes(x.lastSend.state));
  useEffect(() => {
    if (!agentBusy && !sending) return;
    const t = setInterval(() => {
      if (document.visibilityState === "visible") void loadReady();
    }, POLL_MS);
    return () => clearInterval(t);
  }, [agentBusy, sending, loadReady]);

  const draftAgain = async () => {
    try {
      await readyApi.draftAgain(goalId, "emails");
      say("The agent is writing the emails again.");
    } catch (e) {
      say(message(e), false);
    } finally {
      await loadReady();
    }
  };

  // Why: when and how fast cold email goes out is the goal's setting, so a change is the goal's next version.
  const changeSending: ChangeSending = async (patch, line) => {
    const cur = view?.current;
    if (!cur) return false;
    const email = cur.config.channels.email!;
    const next = { ...cur.config, channels: { ...cur.config.channels, email: { ...email, settings: { ...email.settings, ...patch } } } };
    try {
      await ccApi.apply(goalId, cur.version, next, line);
      setView(await goalsApi.get(goalId));
      await loadReady();
      say(line);
      return true;
    } catch (e) {
      say(message(e), false);
      return false;
    }
  };
  const plan = view?.current?.plan ?? null;
  const ms = plan ? mailStart(plan) : null;
  const d = planDay(ms?.when, new Date());
  const mailDay = d ? longDay(d) : null;

  if (native)
    return (
      <ReadyNative
        goalId={goalId}
        view={view}
        loadError={loadError}
        ready={ready}
        approved={approved}
        product={product}
        conns={conns}
        startDay={mailDay}
        setReady={setReady}
        reload={loadReady}
        reloadGoal={async () => setView(await goalsApi.get(goalId))}
        changeSending={changeSending}
        onDraftAgain={() => void draftAgain()}
        say={say}
        toast={toast}
      />
    );

  if (loadError && !view) return <div className="wrap ga rdy"><p className="load-note">{loadError}</p></div>;
  if (!view || !g) return <div className="wrap ga rdy"><p className="load-note">Loading Get ready…</p></div>;

  const head = (extra?: ReactNode) => (
    <header className="ph">
      <div className="ctx">
        <BackLink to={`/goals/${goalId}`} label="Goal" />
        {product && (
          <span className="crumbs">
            <Link className="prod" to={`/products/${product.id}`}>
              <ProductMark product={product} />
              <span className="pn">{product.name}</span>
            </Link>
          </span>
        )}
        {extra}
      </div>
      <div className="ph-t">
        <h1 className="ptitle">Getting ready</h1>
      </div>
      {approved && (
        <p className="ph-s goalref">
          <Icon name="target" />
          <span>{g.sentence}</span>
        </p>
      )}
    </header>
  );

  if (!approved)
    return (
      <div className="wrap ga rdy">
        {head()}
        <section className="card clear">
          <Icon name="clock" />
          <span>
            This page opens once you approve a plan.{" "}
            <Link className="ch-lnk" to={`/goals/${goalId}`}>
              Go to the plan
            </Link>
          </span>
        </section>
        {toast}
      </div>
    );

  const config = view.current!.config;
  if (!config.channels.email?.on) return <Navigate to={`/goals/${goalId}/audience`} replace />;
  const email = conns?.find((c) => c.kind === "email");

  const parts: Part[] = ready
    ? [
        { id: "cx", name: "Connections", ok: connectionReady(email), agent: shownState(email) === "checking" },
        {
          id: "mail",
          name: "Cold email",
          ok: listsReady(ready.lists) && ready.emails.length > 0 && ready.emails.every((e) => e.approved) && connectionReady(email),
          agent: listsBuilding(ready.lists) || drafting(ready.runs.emails),
        },
      ]
    : [];
  const part = (id: string) => parts.find((p) => p.id === id)!;

  return (
    <div className="wrap ga rdy">
      {head(
        plan && (
          <span className="ctx-r">
            <Link className="chip link" to={`/goals/${goalId}/plan`}>
              Read the plan
              <Icon name="right" />
            </Link>
          </span>
        ),
      )}
      {!ready ? (
        <p className="load-note">Loading Get ready…</p>
      ) : (
        <>
          <section className="card rd-top">
            <div className="rd-sum">
              <b>
                {parts.filter((p) => p.ok).length} of {parts.length} ready
              </b>
              <span>{mailDay ? `Cold email starts ${mailDay}.` : ""}</span>
              {ready.metaOn && (
                <Link className="chip link" to={`/goals/${goalId}/ads`}>
                  Ads
                  <Icon name="right" />
                </Link>
              )}
            </div>
            <ol className="rd-seg" style={{ gridTemplateColumns: `repeat(${parts.length},minmax(0,1fr))` }}>
              {parts.map((p) => (
                <li key={p.id} className={p.ok ? "ok" : ""}>
                  <button type="button" onClick={() => jump(p.id)}>
                    <i />
                    <span>{p.name}</span>
                  </button>
                </li>
              ))}
            </ol>
          </section>

          <section className="card rd-sec" id="rd-cx">
            <div className="rd-h">
              <h3 className="h-i">
                Connections
                <Info>Flashadz checks each one itself. It turns green only when the checks pass.</Info>
              </h3>
              <Who p={part("cx")} />
            </div>
            <div className="rows">
              <CxRows productId={g.productId} conns={conns} kinds={["email"]} reload={reloadCx} say={say} />
            </div>
          </section>

          {ready.emailOn && (
            <ColdEmail
              goalId={goalId}
              view={ready}
              startDay={mailDay}
              mailboxReady={connectionReady(email)}
              who={<Who p={part("mail")} />}
              setView={setReady}
              reload={loadReady}
              onDraftAgain={() => void draftAgain()}
              say={say}
              timezones={(config.channels.email?.settings as { timezones?: Record<string, string> } | undefined)?.timezones ?? {}}
              changeSending={changeSending}
            />
          )}
        </>
      )}
      {toast}
    </div>
  );
}
